import { apiFetch } from '../api-client';

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | 'other';
export type AccountBalanceSide = 'debit' | 'credit';

export interface Account {
  code: string;
  name: string;
  parentCode: string | null;
  type: AccountType;
  defaultBalance: AccountBalanceSide;
  isLeaf: boolean;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountTreeNode extends Account {
  children: AccountTreeNode[];
}

export interface AccountsSummary {
  total: number;
  inactive: number;
  leafCount: number;
  byType: Partial<Record<AccountType, number>>;
}

export interface ListAccountsResponse<T = Account | AccountTreeNode> {
  items: T[];
  summary: AccountsSummary;
}

export interface ListAccountsQuery {
  type?: AccountType;
  activeOnly?: 'true' | 'false';
  mode?: 'flat' | 'tree';
  search?: string;
}

export interface CreateAccountInput {
  code: string;
  name: string;
  parentCode?: string;
  type: AccountType;
  defaultBalance: AccountBalanceSide;
  isLeaf?: boolean;
  active?: boolean;
  notes?: string;
}

export interface UpdateAccountInput {
  name?: string;
  active?: boolean;
  defaultBalance?: AccountBalanceSide;
  notes?: string;
}

function qs(query: ListAccountsQuery): string {
  const p = new URLSearchParams();
  if (query.type) p.set('type', query.type);
  if (query.activeOnly) p.set('activeOnly', query.activeOnly);
  if (query.mode) p.set('mode', query.mode);
  if (query.search) p.set('search', query.search);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const chartOfAccountsApi = {
  async listFlat(query: Omit<ListAccountsQuery, 'mode'> = {}): Promise<ListAccountsResponse<Account>> {
    return apiFetch(`/api/chart-of-accounts${qs({ ...query, mode: 'flat' })}`);
  },
  async listTree(query: Omit<ListAccountsQuery, 'mode'> = {}): Promise<ListAccountsResponse<AccountTreeNode>> {
    return apiFetch(`/api/chart-of-accounts${qs({ ...query, mode: 'tree' })}`);
  },
  async findOne(code: string): Promise<Account & { children: Account[] }> {
    return apiFetch(`/api/chart-of-accounts/${encodeURIComponent(code)}`);
  },
  async create(input: CreateAccountInput): Promise<Account> {
    return apiFetch(`/api/chart-of-accounts`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  async update(code: string, input: UpdateAccountInput): Promise<Account> {
    return apiFetch(`/api/chart-of-accounts/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  async remove(code: string): Promise<{ ok: true }> {
    return apiFetch(`/api/chart-of-accounts/${encodeURIComponent(code)}`, {
      method: 'DELETE',
    });
  },
};

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  asset: 'Tài sản',
  liability: 'Nợ phải trả',
  equity: 'Vốn chủ sở hữu',
  revenue: 'Doanh thu',
  expense: 'Chi phí',
  other: 'Xác định KQKD',
};

export const ACCOUNT_TYPE_ORDER: AccountType[] = [
  'asset',
  'liability',
  'equity',
  'revenue',
  'expense',
  'other',
];

export const BALANCE_LABEL: Record<AccountBalanceSide, string> = {
  debit: 'Dư Nợ',
  credit: 'Dư Có',
};
